import { Injectable } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { ToastController } from 'providers/toast-controller';
import type { MyApp } from 'app/app.component';
import * as Sentry from '@sentry/angular';
import { AppEnvironment } from 'environments/environment';
import { registerSentryTestTrafficProvider, wasEventDropped } from 'app/app-sentry';
import { isCanaryEnv, isE2ETest } from 'environments/environment-helpers';
import { DateTime } from 'luxon';
import type { FatalErrorPageParams } from 'pages/fatal-error/fatal-error-interface';
import { getPageInterface } from 'app/app-page-helpers';
import { ToastOptions } from 'components/toast/toast-host';
import { TranslatedString } from 'models/translated-string';
import { TelemetryService } from './telemetry.service';
import { NavController } from 'providers/nav-controller';
import { Log_WorkoutExercise, healSparseLogSets } from '@cf-shared/data-model/log-workout';
import { describeContextForLogLine } from '@cf-shared/error-describe';
import * as crypto from 'crypto';
import { ConnectionState, OnlineStateService, connectionStateChangedEvent } from './online-state.service';
import { PubSubService } from './pubsub.service';
import { isRetriableHttpError } from './cloud-functions-retry';
import type { DatabaseWrapperProvider } from './database-wrapper';
import { DatabaseRecoveryService } from './database-recovery.service';
import { isDatabaseShuttingDown, isDatabaseUnavailable } from './database-unavailable-error';
import { reportDatabaseUnavailableError } from 'app/sentry-error-policy';

// pure error-detection functions live in error-patterns.ts (Angular-free, unit-testable)
import { isIdbConnectionError, isIdbError, isClosedDatabaseHandleError, isTransientFetchError, isFirebaseAuthNetworkError, isCouchHttpErrorObject, isCouchUsersDocConflictError, errorReportFields, isThirdPartyError } from './error-patterns';
// the three severity tiers (#4382) live in their own Angular-free module so they stay unit-testable
import { MixpanelCopy, ProblemKind, PROBLEM_KIND_TAG, SentryReportTags, SilentReportShape, describeSilentReport, silentReportConsoleLine } from './problem-kind';
import { noteSilentProblem, noteSomethingWentWrong, SessionHealth } from './session-health';
import { FatalErrorReport, FatalErrorReportParams, describeFatalErrorReport } from './fatal-error-report';
// the once-per-device-per-day INFO cap (#6007), kept pure so it is testable without Sentry
import { INFO_CAP_STORAGE_KEY, INFO_CAP_STORAGE_REPORT, InfoDailyCap, InfoReportResult } from './info-daily-cap';
export { isIdbConnectionError, isIdbError, isClosedDatabaseHandleError, isTransientFetchError, isFirebaseAuthNetworkError, isCouchHttpErrorObject, errorReportFields, isThirdPartyError };


interface CaptureResult {
  sentryId: string;
  silenced: boolean; // whether the throttling system decided not to report this
}
interface SilencerInfo {
  silencedCounter: number;
  lastReportedTime: number;
}

@Injectable()
export class LoggingService {
  private app: MyApp;
  private telemetry: TelemetryService;
  // one per session: once storage fails, the cap keeps its state in memory for the rest of it
  private infoDailyCap = new InfoDailyCap({
    read: () => localStorage.getItem(INFO_CAP_STORAGE_KEY),
    write: value => localStorage.setItem(INFO_CAP_STORAGE_KEY, value),
  });

  constructor(
    private toastCtrl: ToastController,
    private navCtrl: NavController,
    private onlineStateService: OnlineStateService,
    private pubSubService: PubSubService,
    private databaseRecovery: DatabaseRecoveryService
  ) {
    // We used to hand ourselves to the database-recovery service here, because it could not
    // take us through its own constructor — we call each other, and Angular cannot build a
    // dependency cycle. It now asks Angular for us at the moment it reports something, which
    // needs no handover and no ordering argument: see `RECOVERY_REPORTING_SERVICE` in
    // `recovery-reporting-service.ts`. The `OnlineStateService` registration in `initialize`
    // below is still the old shape.
  }

  initialize(app: MyApp, telemetry: TelemetryService, dbProvider: DatabaseWrapperProvider) {
    this.app = app;
    this.telemetry = telemetry;

    // hand the database-recovery service the rest of what it needs, now that the app
    // component exists
    this.databaseRecovery.initialize(app, telemetry, dbProvider);

    // Register with OnlineStateService to avoid circular dependency
    this.onlineStateService.setLoggingService(this);

    // df-a0223e8ffec8: let the module-level Sentry hook tag every event with whether
    // this session is test traffic. That hook runs before Angular DI exists, so it
    // cannot inject the telemetry service — and it is registered from HERE, rather than
    // from `TelemetryService`'s own constructor, because this file already imports
    // `app/app-sentry` (`wasEventDropped` above), so no new import edge is created.
    registerSentryTestTrafficProvider(() => telemetry.isTestTraffic());

    this.initConsoleCapture();
    this.initOnlineStateHandling();
  }

  private initOnlineStateHandling() {
    // Subscribe to connection state changes
    this.pubSubService.subscribe(connectionStateChangedEvent, (newState) => {
      void this.handleConnectionStateChange(newState);
    });
  }

  private async handleConnectionStateChange(newState: ConnectionState) {
    // When app comes online, flush the Sentry queue
    if (newState === ConnectionState.Online) {
      console.log('App came online, flushing Sentry queue...');
      try {
        const flushedSuccessfully = await Sentry.flush(5000); // 5 second timeout
        if (flushedSuccessfully) {
          console.log('Sentry queue flushed successfully');
        } else {
          console.warn('Sentry queue flush timed out after 5 seconds');
        }
      } catch (error) {
        console.warn('Error flushing Sentry queue:', error);
      }
    }
  }

  /*
  Note: Sentry would normally captures console logs, but in prod we want to not show the logs to the user.
  So when we intercept them and discard (so they don't appear in the console) we prevent Sentry 
  from capturing them. 
  
  This is why we don't use Sentry's own console interceptor, but instead we replace the console.log with our own.

  Note, we could have arranged to keep Sentry's own interceptor in non-prod, but then it would have been
  hard to test the Sentry integration in non-prod. Now, since we are disabling the Sentry interceptor in general
  we need to then intercept not just log, BUT ALSO warning and error!
  */
  private initConsoleCapture() {


    // this should never happen, but just in case
    if (!window || !window.console || !window.console.log) {
      console.error('LoggingService: window is not available!');
      return;
    }

    type ConsoleLevelsType = 'info' | 'log' | 'warn' | 'error';
    const allConsoleLevels = ['log', 'info', 'warn', 'error'];

    // remember all the original console functions
    let orgConsoleFuncs: { [key: string]: (...args: any[]) => void } = {}
    for (let level of allConsoleLevels) {
      orgConsoleFuncs[level] = console[level].bind(console);
    }

    type ConsoleLevelMapping = {
      sentryType: 'info' | 'debug',
      sentryLevel: Sentry.SeverityLevel,
      allowedInProd: boolean,
    };

    const levelMappings: { [key: string]: ConsoleLevelMapping } = {
      'info': { sentryType: 'debug', sentryLevel: 'info', allowedInProd: true },
      'log': { sentryType: 'debug', sentryLevel: 'log', allowedInProd: false },
      'warn': { sentryType: 'debug', sentryLevel: 'warning', allowedInProd: true },
      'error': { sentryType: 'debug', sentryLevel: 'error', allowedInProd: true },
    };

    let messageRouter = (level: ConsoleLevelsType, ...args: any[]) => {
      let levelMapping = levelMappings[level] || levelMappings['error'];

      // if this level is allowed in the current environment (canary is compiled as prod but should show all logs)
      if (AppEnvironment.name !== 'prod' || isCanaryEnv() || levelMapping.allowedInProd) {
        // call the original console function
        orgConsoleFuncs[level](...args);
      }

      // catch angular recompiles
      if (args[0] === '[webpack-dev-server] App updated. Recompiling...') {
        this.toastCtrl.showToast({
          message: LL.system.messages.recompiling(),
          uniqueId: 'app-recompiling-toast',
          showCloseButton: false,
          duration: 30000, // don't auto-dismiss
        });
      }

      /*
      Preparing the message for Sentry... 
      */

      // emulate what the browser console does, by joining all the arguments into a single string
      // this will present complex objects as just [object] etc
      let message = args.join(' ');

      let breadcrumb: Sentry.Breadcrumb = {
        type: levelMapping.sentryType,
        category: 'console',
        message: message,
        level: levelMapping.sentryLevel,
      };

      // if the message is not just text, but contains objects, we want to pass the objects to Sentry
      if (args.length > 1 || typeof args[0] !== 'string') {
        // but in 99% of calls, the 0th element would just be same as `message`, so we can skip it if that's the case
        let cleanArgs = args;
        if (args[0] === message || message.startsWith(args[0])) {
          cleanArgs = args.slice(1);
        }

        if (cleanArgs.length > 0) {

          // for each argument
          for (let i = 0; i < cleanArgs.length; i++) {
            let arg = cleanArgs[i];
            // if it's an exception
            if (arg instanceof Error) {
              // pass just the message and stack
              /* This is because we can't serialize the zone, promise, etc quagmires. */
              cleanArgs[i] = { message: arg.message, stack: arg.stack };
            }
          }

          if (AppEnvironment.name !== 'prod') {
            try {
              // try to serialize the objects, avoiding circles, to see if Sentry actually crashes inside when doing that
              cleanArgs = JSON.parse(JSON.stringify(cleanArgs));
            } catch (e) {
              cleanArgs = ['(error serializing console args)', e];
              this.trackAbnormalException(e);
              orgConsoleFuncs['error']('Error serializing console args', e);
            }
          }
          breadcrumb.data = { ...cleanArgs };
        }
      }

      // add a sentry breadcrumb for the log, to make sure we have better context for crash reports
      Sentry.addBreadcrumb(breadcrumb);
    }


    // replace all the console functions with our own
    for (let level of allConsoleLevels) {
      console[level] = (...args: any[]) => messageRouter(level as ConsoleLevelsType, ...args);
    }
  }

  silencers: { [fingerprint: string]: SilencerInfo } = {};

  readonly MIN_TIME_BETWEEN_REPORTS = 1000 * 60; // 1 minute

  private resetSilencer(fingerprint: string) {
    let silencer = this.silencers[fingerprint];
    if (!silencer) {
      this.trackAbnormalError(`LoggingService: resetSilencer called for unknown fingerprint: ${fingerprint}`);
      return;
    }
    // if something was silenced in the meantime
    if (silencer.silencedCounter > 0) {
      // report it to telemetry
      console.log(`Silenced ${silencer.silencedCounter} reports for fingerprint ${fingerprint} in the last ${Math.round((Date.now() - silencer.lastReportedTime) / 1000)} seconds`);
      this.telemetry.trackEvent('sentry_errors_silenced', { count: silencer.silencedCounter });
    }

    // reset the counter and time
    silencer.silencedCounter = 0;
    silencer.lastReportedTime = Date.now();
  }

  /** Reset all silencers, so that next report will not be silenced. */
  private resetAllSilencers() {
    console.log('Resetting all silencers');
    // note, we must walk them to report the silenced counts...
    for (let fingerprint in this.silencers) {
      this.resetSilencer(fingerprint);
    }
    // ... before we clear the list
    this.silencers = {};
  }

  // calculate fingerprint of a sentry problem to make sure we only silence repeated reports of the same type
  // signature is an object that gets checksummed to identify the specific error
  private calculateFingerprint(signature: any): string {
    // convert object to string
    let str = '';
    try {
      str = JSON.stringify(signature);
    } catch (e) {
      console.error('Error serializing signature for fingerprint:', e);
    }
    // hash it to a fixed length
    let hash = crypto.createHash('SHA1').update(str).digest('hex');
    return hash;
  }

  // check whether this report should be silenced
  shouldSilence(fingerprint: string): boolean {
    let silencer = this.silencers[fingerprint];
    if (!silencer) {
      // create a new silencer for this fingerprint
      silencer = {
        silencedCounter: 0,
        lastReportedTime: 0,
      };
      this.silencers[fingerprint] = silencer;
    }

    // if the last report was too recent
    let now = Date.now();
    if (now - silencer.lastReportedTime < this.MIN_TIME_BETWEEN_REPORTS) {
      if (silencer.silencedCounter === 0) {
        // if this is the first silenced report, report it to telemetry
        console.log('Silencing first report for fingerprint:', fingerprint);
        this.telemetry.trackEvent('sentry_errors_silenced_first', { time_since_last: now - silencer.lastReportedTime, fingerprint });
      }
      // just count, and don't report this one
      silencer.silencedCounter++;
      console.log(`Silencing repeated report #${silencer.silencedCounter} for fingerprint:`, fingerprint);
      return true;
    }

    // if no recent reports, reset the counter and report this one
    this.resetSilencer(fingerprint);
    return false;
  }

  private captureException(err: any, kind: ProblemKind): CaptureResult {
    let fingerprint = this.calculateFingerprint(err.message || err.toString());
    let silenced = this.shouldSilence(fingerprint);
    // tag the event with its severity tier (#4382) so Sentry can filter real problems
    // apart from informational self-heal records
    let sentryId = silenced ? '' : Sentry.captureException(err, { tags: { [PROBLEM_KIND_TAG]: kind } });
    // if beforeSend dropped the event, treat as silenced (don't show toast for orphaned IDs)
    if (sentryId && wasEventDropped(sentryId)) {
      return { sentryId: '', silenced: true };
    }
    return { sentryId, silenced };
  }

  private captureMessage(message: string, level: Sentry.SeverityLevel, kind: ProblemKind, extraTags?: SentryReportTags, extra?: Record<string, unknown>): CaptureResult {
    let fingerprint = this.calculateFingerprint(message);
    let silenced = this.shouldSilence(fingerprint);
    // the caller's tags are spread FIRST and the tier LAST, so a caller can never
    // overwrite `problem_kind` — see the SentryReportTags comment for why that matters
    let sentryId = silenced ? '' : Sentry.captureMessage(message, { level, tags: { ...extraTags, [PROBLEM_KIND_TAG]: kind }, extra });
    // if beforeSend dropped the event, treat as silenced (don't show toast for orphaned IDs)
    if (sentryId && wasEventDropped(sentryId)) {
      return { sentryId: '', silenced: true };
    }
    return { sentryId, silenced };
  }

  /*
  The `logMessage` is the message that will is used only for filtering. It is not shown to the user.
  */
  private showAbnormalToast(opts: { sentryId: string, logMessage: string, userMessage?: TranslatedString }) {
    if (!this.shouldShowAbnormalToastForMessage(opts.logMessage)) {
      console.log(`Abnormal toast suppressed for Sentry event: "${opts.sentryId}"`);
      return;
    }

    console.log(`Showing abnormal toast for Sentry event: "${opts.sentryId}"`);

    let message: TranslatedString, actionText: TranslatedString, closeText: TranslatedString;
    if (opts.userMessage) {
      message = opts.userMessage;
      actionText = LL.common.buttons.report();
      closeText = LL.common.buttons.dismiss();
    } else {
      message = LL.errors.userFacing.noticeUnusual();
      actionText = LL.common.buttons.yes();
      closeText = LL.common.buttons.no();
    }
    let toastOps: ToastOptions = {
      uniqueId: 'abnormal-report-toast',
      message,
      showCloseButton: true,
      onAction: () => {
        this.resetAllSilencers();
        // do the same as if the user clicked the bug report FAB, just pass the pre-generated sentryId
        void this.app.reportIssueFromToastAsync(opts.sentryId);
      },
      onClose: () => {
        this.resetAllSilencers();
      },
      actionButtonText: actionText,
      closeButtonText: closeText,
    };

    this.toastCtrl.showToast(toastOps);
  }

  /* What the app does when its local database breaks — deciding whether a failure is one
   * we can recover from, reopening the databases, reloading the page — is NOT this
   * service's job. It lives in `DatabaseRecoveryService` (df-16fe419d737b), and every
   * caller reaches that service directly (df-0630dafeff8d).
   *
   * This service used to carry a forwarding method for each of those calls, so that the
   * split could land without moving forty call sites in the same commit. They are gone.
   * Do not add one back: a method of that concern on this service is what made a reader
   * have to work out which of the two jobs they were standing in, and it is a place the
   * concern can accrete back. `database-recovery.service.test.ts` fails if one reappears.
   *
   * This service still holds the recovery service itself, for the one use below and for
   * the constructor handover — it is the reporting route recovery files its findings
   * through.
   */

  /** Call this from any catch handler where such exception shouldn't happen in production. */
  public trackAbnormalException(err: any, userMessage?: TranslatedString) {
    // deliberately overcautious: check for IndexedDB errors here as a safety net,
    // in case any caller forgot to call interceptDatabaseError() first
    if (this.databaseRecovery.interceptDatabaseError(err)) {
      // a real database failure, handled elsewhere but still a problem this
      // session hit (#4625). Set here rather than at the bottom, because this
      // branch returns before the reporting path below ever runs.
      //
      // Not when we are deleting the databases on purpose, though: this same
      // branch catches the errors a deliberate wipe produces, and the code that
      // decides so says plainly they are expected rather than a fault.
      if (!this.databaseRecovery.isIntentionalDestruction()) {
        noteSomethingWentWrong();
      }
      return;
    }

    // a refused database read or write goes through the same split the global handlers use
    // (sentry-error-policy.ts), whoever caught it. Without this, a page's catch-all that
    // forwards here turned a sign-out refusal into a Sentry exception, a console.error and
    // a toast (#7024). Exception: a caller that passed its own message for the user (a save
    // the user started) keeps that toast when the refusal is not our own sign-out, so the
    // user still hears that the save failed
    const ourOwnSignOut = isDatabaseUnavailable(err)
      && (isDatabaseShuttingDown(err) || this.databaseRecovery.isIntentionalDestruction());
    if (isDatabaseUnavailable(err) && (ourOwnSignOut || !userMessage)) {
      reportDatabaseUnavailableError(err, this.databaseRecovery, this);
      return;
    }

    // cloud function network and retriable-5xx errors are already handled
    // by the CF wrapper (retried per resilient schedule, SilentProblem
    // reported on exhaustion). Dedup here so a caller's catch handler
    // doesn't double-report. The 5xx dedup uses isRetriableHttpError as
    // single source of truth — only the statuses the wrapper actually
    // retried + reported (502/503) are deduped; a 500 or 504 falls
    // through the wrapper without retry / without Sentry, so it must
    // NOT be deduped here.
    if (err?.name === 'CloudFunctionNetworkError') {
      return;
    }
    if (err?.name === 'CloudFunctionHttpError' && isRetriableHttpError(err)) {
      return;
    }

    if (!(err instanceof Error)) {
      // if the error is not an exception, report as error
      this.trackAbnormalError(err, userMessage);
      return;
    }

    try {
      let { sentryId, silenced } = this.captureException(err, ProblemKind.Error);
      if (silenced) {
        return;
      }
      // a real caught exception that we are actually reporting (#4625).
      //
      // Three earlier returns deliberately never reach here. The
      // CloudFunctionNetworkError and retriable-5xx ones are normal in an
      // offline-first app, and counting either would suppress the review prompt
      // after every network blip. The `silenced` return just above is the third:
      // a report the throttler dropped is one we decided is noise. That last
      // one costs nothing either way, because the FIRST occurrence of a
      // fingerprint is never silenced, so a genuine problem always sets the
      // flag on its way past. `trackAbnormalError` sets the flag BEFORE its own
      // identical silenced check, which is the same outcome by a different
      // route - the flag only ever moves one way, so the order cannot matter.
      noteSomethingWentWrong();

      let logMessage = err.toString();
      console.error(`Abnormal exception event (Sentry: ${sentryId}, user: ${userMessage || '<none>'})): `, logMessage);
      console.error(`Error: "${err.message}"\nStack: `, err.stack);
      this.telemetry.trackEvent('sentry_error', { sentry_id: sentryId, user_message: userMessage, log_message: logMessage, kind: ProblemKind.Error });
      this.showAbnormalToast({ sentryId, logMessage, userMessage });
      //debugger;
    } catch (err2) {
      // ignore errors while tracking errors, otherwise we lock the user up
      // (trackAbnormalException cannot report its own failure: that would re-enter the code that just threw)
      console.error("Recursive error:", err2)
    }
  }

  shouldShowAbnormalToastForMessage(logMessage: string) {
    let messagesToIgnore = [
      // none are needed for now
      //  /.*Error: mix\/user.* the change conflicts with externally synchronized data!.*/,
      //  /.*mix\/user.* the change conflicts with externally synchronized data!.*/,
      /.*DayEnd - the change conflicts with externally sync.*/
    ]
    if (messagesToIgnore.some(r => r.test(logMessage))) {
      return false;
    }
    return true;
  }

  /** Call this from any non-catch error handler where such error shouldn't happen in production. */
  public trackAbnormalError(logMessage: any, userMessage?: TranslatedString) {
    // the self-healing login-record conflict — `isCouchUsersDocConflictError` says what it
    // is. Info tier for the same reason CloudFunctionNetworkError returns early above:
    // handled and reported elsewhere, so a report here is a duplicate that also interrupts
    // the user. It does NOT claim the repair succeeded — it has only been tried — and what
    // keeps that honest is that a repair which keeps failing still reports loudly from
    // `getFoodByIdAsync network error` (while the app is in the foreground and online) and
    // `Auth: Couch login - too many retries`.
    //
    // Two things to keep true. It suppresses REPORTING only: every caller still takes its
    // own failure path. And it must stay ABOVE `noteSomethingWentWrong()` below, or a race
    // that fixes itself would suppress the review prompt (#4625). One check covers both
    // entry points, because `trackAbnormalException` hands every non-`Error` value here and
    // the predicate matches nothing else.
    if (isCouchUsersDocConflictError(logMessage)) {
      this.trackSilentInfo('CouchUsersDocConflictDuringRead', {
        status: logMessage.status,
        reason: logMessage.reason,
        docId: (logMessage as { docId?: string }).docId,
      });
      return;
    }

    // a real failure that was not an exception (#4625). Unlike
    // trackAbnormalException above, this method has one early return — the
    // self-healing login-record conflict just above, which is deliberately not
    // counted as something going wrong — so this is the one place to set it for
    // every other failure.
    noteSomethingWentWrong();
    try {
      if (typeof logMessage !== 'string') {
        // if the message is not a string, convert it to a string
        let serialized = JSON.stringify(logMessage);

        // if JSON.stringify produced empty result, extract known error properties
        // (e.g. PouchDB errors have non-enumerable properties that don't serialize)
        if (!serialized || serialized === '{}' || serialized === '[]') {
          const props: string[] = [];
          for (const key of ['message', 'name', 'status', 'reason', 'error', 'code', 'docId']) {
            if ((logMessage as any)[key] !== undefined) {
              props.push(`${key}: ${(logMessage as any)[key]}`);
            }
          }
          serialized = props.length > 0
            ? `{${props.join(', ')}}`
            : `(non-serializable: ${Object.prototype.toString.call(logMessage)})`;
        }

        logMessage = serialized;
      }

      let { sentryId, silenced } = this.captureMessage(logMessage, 'error', ProblemKind.Error);
      if (silenced) {
        return;
      }

      console.error(`Abnormal error event (Sentry: ${sentryId}, user: ${userMessage || '<none>'})`, logMessage);
      this.telemetry.trackEvent('sentry_error', { sentry_id: sentryId, user_message: userMessage, log_message: logMessage, kind: ProblemKind.Error });
      this.showAbnormalToast({ sentryId, logMessage, userMessage });
    } catch (err2) {
      // ignore errors while tracking errors, otherwise we lock the user up
      // (trackAbnormalError cannot report its own failure: that would re-enter the code that just threw)
      console.error("Recursive error:", err2)
    }

  }

  /**
   * Report something to Sentry and Mixpanel without interrupting the user.
   *
   * Shared by the two silent tiers (#4382). Everything they do is identical — same
   * breadcrumb, same fingerprint-based rate limiting, same never-throw contract — except
   * what `describeSilentReport` decides: the Sentry message prefix, the Sentry severity
   * level, which `console` function writes the local line, and whether the Mixpanel row
   * is sent.
   */
  private trackSilentReport(
    kind: ProblemKind.Info | ProblemKind.Problem,
    reportName: string,
    context: any,
    tags?: SentryReportTags,
    mixpanelCopy: MixpanelCopy = MixpanelCopy.Send,
  ) {
    // if the context is not an object
    if (context && typeof context !== 'object') {
      // wrap it in an object, otherwise Sentry will report a "Event processing error"
      context = { context: context };
    }
    try {
      const shape = describeSilentReport(kind, reportName, mixpanelCopy);

      // log the context into a breadcrumb
      const addBreadcrumb = (data: any) => {
        let breadcrumb: Sentry.Breadcrumb = {
          type: 'debug',
          category: 'problem',
          level: 'debug',
          message: reportName,
          data,
        };
        Sentry.addBreadcrumb(breadcrumb);
      };

      // if this is an INFO report, it goes to Sentry at most once per device per day,
      // and never from an E2E run (#6007, design-docs/telemetry.md)
      const capped = kind === ProblemKind.Info ? this.reportThroughInfoCap(reportName, context, shape, tags, addBreadcrumb) : undefined;
      let sentryId: string;
      if (capped) {
        this.reportInfoCapStorageProblem(reportName, capped.storageProblem);
        if (!capped.writeConsoleLine) {
          return;
        }
        sentryId = capped.sentryId;
      } else {
        addBreadcrumb(context);
        // cause a sentry event
        const captured = this.captureMessage(shape.sentryMessage, shape.sentryLevel, kind, tags);
        if (captured.silenced) {
          return;
        }
        sentryId = captured.sentryId;
      }
      const capAllowsMixpanelRow = capped ? capped.sendMixpanelRow : true;

      // Log to console, at the level this tier calls for. The wording is built from the
      // tier value so the problem tier keeps its EXACT pre-#4382 line ("Silent problem
      // (Sentry: ..., problem: ...)") — several E2E specs allow-list console errors by
      // matching that exact phrase, and rewording it would silently break every one.
      // The context goes as TEXT and not as the object itself: an object argument is
      // rendered as the literal "[object Object]" by Puppeteer's msg.text(), which is
      // what the E2E browser log is built from, so every field in it used to be lost on
      // the way into that log — and in an E2E run there is no Sentry event to fall back
      // on (df-2ff64a6cfd69). The text is single-line, redacted and length-capped.
      console[shape.consoleLevel](
        silentReportConsoleLine(kind, sentryId, reportName),
        describeContextForLogLine(context));
      // if the caller asked for this report to stay out of Mixpanel, or the daily cap held it
      // back, no row is sent
      if (shape.sendsMixpanelRow && capAllowsMixpanelRow) {
        this.telemetry.trackEvent('sentry_error', { sentry_id: sentryId, log_message: reportName, kind });
      }
    } catch (err2) {
      // ignore errors while tracking errors, otherwise we lock the user up
      // (trackSilentReport cannot report its own failure: that would re-enter the code that just threw)
      console.error("Recursive error:", err2)
    }
  }

  /** Run an INFO report through the daily cap, with the real storage, clock and Sentry calls. */
  private reportThroughInfoCap(
    reportName: string,
    context: any,
    shape: SilentReportShape,
    tags: SentryReportTags | undefined,
    addBreadcrumb: (data: any) => void,
  ): InfoReportResult {
    return this.infoDailyCap.report({
      name: reportName,
      context,
      isE2E: isE2ETest(),
      today: DateTime.local().toISODate() ?? '',
      addBreadcrumb,
      capture: (extra, capTags) => {
        const captured = this.captureMessage(shape.sentryMessage, shape.sentryLevel, ProblemKind.Info, { ...tags, ...capTags }, extra);
        return { sent: !captured.silenced, sentryId: captured.sentryId };
      },
    });
  }

  /**
   * Say once that the INFO cap's storage failed or held a damaged value. The cap has
   * already fallen back to memory or repaired the value, so this is a repair note, not a
   * problem. Never sent for this report itself, so a failure here cannot feed itself.
   */
  private reportInfoCapStorageProblem(reportName: string, problem: InfoReportResult['storageProblem']) {
    if (problem && reportName !== INFO_CAP_STORAGE_REPORT) {
      this.trackSilentInfo(INFO_CAP_STORAGE_REPORT, problem);
    }
  }

  /**
   * Tier (a) — record that an automatic repair ran, or that a rare-but-expected condition
   * was observed. NOTHING IS WRONG and nobody needs to look at it (#4382).
   *
   * Use this only when the surrounding code PROVES the condition was already handled by
   * the time this line runs. If the condition is still unresolved and a person may
   * eventually need to act on it, use `trackSilentProblem` instead — picking the wrong one
   * here is how a real problem goes quiet.
   *
   * `mixpanelCopy` defaults to sending the Mixpanel `sentry_error` row. Pass
   * `MixpanelCopy.Skip` only for a report whose rate is read in Sentry and whose Mixpanel
   * rows nobody uses — see `MixpanelCopy` in problem-kind.ts.
   */
  public trackSilentInfo(infoName: string, context: any, mixpanelCopy: MixpanelCopy = MixpanelCopy.Send) {
    this.trackSilentReport(ProblemKind.Info, infoName, context, undefined, mixpanelCopy);
  }

  /**
   * Tier (b) — report a condition that is NOT solved, and that a person may eventually
   * need to act on, but that we deliberately do not interrupt the user for.
   *
   * If the thing being reported is an automatic repair that already succeeded, it belongs
   * in `trackSilentInfo` instead (#4382).
   *
   * `tags` (#4537) is optional and adds low-cardinality Sentry TAGS to this one report,
   * so a person can break the issue down by them in the Sentry UI. It is not a second
   * `context`: read `SentryReportTags` in problem-kind.ts before adding one, because the
   * two destinations are completely different and a high-cardinality tag is a mistake.
   *
   * `sessionHealth` defaults to counting this report as something going wrong in the
   * person's session, which stops the app asking them to rate it. Pass
   * `SessionHealth.NotTheUsersSession` only for a report that is real but says nothing
   * about this person's session, and say why at the call site — see `SessionHealth` in
   * session-health.ts.
   */
  public trackSilentProblem(problemName: string, context: any, tags?: SentryReportTags, sessionHealth: SessionHealth = SessionHealth.Counts) {
    // something is wrong and unresolved, and the person was deliberately not
    // interrupted (#4625). Routed through noteSilentProblem rather than set
    // directly, because some reports arrive at this tier without meaning the
    // person's session went wrong - the call site says so, or session-health.ts
    // recognises the network kinds by name.
    noteSilentProblem(problemName, sessionHealth);
    this.trackSilentReport(ProblemKind.Problem, problemName, context, tags);
  }

  /**
   * Report a fatal error screen, named after its cause so each cause gets its own Sentry
   * issue, at the tier that cause calls for — see `describeFatalErrorReport`.
   */
  public trackFatalErrorScreen(report: FatalErrorReport, params: FatalErrorReportParams) {
    const shape = describeFatalErrorReport(report, params);
    // if nobody can act on this cause, record it as information rather than a problem
    if (shape.kind === ProblemKind.Info) {
      this.trackSilentReport(ProblemKind.Info, shape.name, params, shape.tags);
      return;
    }
    this.trackSilentProblem(shape.name, params, shape.tags);
  }

  /// Leads the user to the fatal error page with the given error message params.
  public async fatalError(params: FatalErrorPageParams) {
    this.trackFatalErrorScreen(FatalErrorReport.Routed, params);
    this.app.setFatalErrorParams(params);
    // dismiss the loading screen so FatalErrorPage is visible
    await this.app.markAppLoadingAsync(false);
    await this.navCtrl.setRootAsync(getPageInterface('FatalErrorPage'), {});
  }


  /** A wrapper around any async function that might throw an exception - reports the problem silently and returns a given default value. */
  async safeAsync<T>(func: () => Promise<T>, defaultValue: T): Promise<T> {
    try {
      return await func();
    } catch (e) {
      this.trackAbnormalException(e);
      return defaultValue;
    }
  }

  // issue #720: detect and heal sparse `sets` arrays. preserves the original
  // sets[0]==null breadcrumb (existing telemetry signal) and emits a separate
  // `healed` breadcrumb so we can track on-the-fly heal frequency per call site.
  checkWorkoutExerciseValidity(where: string, exercise: Log_WorkoutExercise) {
    if (!exercise.sets || exercise.sets.length === 0) return;
    if (!exercise.sets[0]) {
      // this one stays a PROBLEM (#4382): it names a data state that should never exist,
      // and the report is written before the heal below has run
      this.trackSilentProblem(`Issue #720 - sets[0] is invalid @${where}`, exercise);
    }
    const healedIndices = healSparseLogSets(exercise.sets);
    if (healedIndices.length > 0) {
      // the repair has already happened by the time we get here, so this is informational
      this.trackSilentInfo(`Issue #720 - healed @${where}`, {
        subSlotId: exercise.subSlotId,
        exerciseId: exercise.exerciseId,
        healedIndices,
        setsCount: exercise.sets.length,
      });
    }
  }

  // report suspected adblocker
  trackAdBlocker(url: string, source: string, errorContext: any) {
    // don't track adblockers if offline, because they are likely to be false positives
    if (this.onlineStateService.connectionState === ConnectionState.Offline) {
      return;
    }
    // local asset fetch failures are transient after iOS app suspension — not worth reporting
    if (typeof url === 'string' && url.startsWith('/assets/')) {
      console.log(`Network error status 0 on local asset "${url}" (transient)`);
      return;
    }
    // for errors in actual analytics, just track them silently
    if (typeof url === 'string' && (url.includes('mixpanel.com') || url.includes('/api/t/') || url.includes('refiner.io')
      // this one is used by Google OAuth Sign-In
      || url.includes('https://apis.google.com/js/gen_204'))) {
      // this was flooding the e2e logs
      console.log(`Network error status 0 on analytics URL "${url}"`, {});
      // other errors are more serious, inform the user
    } else {
      this.trackSilentProblem(`Network error status 0 on URL "${url}"`, { source, errorContext });
    }
  }
}
